import { Directional } from '../../../../components/Directional/Directional';
import { Note } from '../../../../components/Note/Note';
import {
  AdSyncNotPosted,
  AdSyncClosed,
  AdSyncPosted,
  AdSyncPrefix,
  AdSyncSource,
  AdSyncSuspended,
  AdSyncUpdated
} from './components.tsx';

# Display job ads

After your software has [synced a position],
the SEEK API can notify you when Ad Sync posts or updates a corresponding job ad.
This is driven by asynchronous [lifecycle events](#lifecycle-events) as SEEK may not process the position sync in real time.

When your software receives a Job Posting event and retrieves the corresponding `PostedPositionProfile` object from our [GraphQL endpoint],
correlate it to a position in your software via [identifiers & references],
then display it in your job ad list view.
This improves hirer understanding and reduces the likelihood that they will inadvertently post a duplicate ad via [classic] or [Enhanced Job Posting].

An open position is not guaranteed to be automatically posted by Ad Sync.
Hirers can specify [rules] to exclude certain positions,
and data [duplication] or quality issues may prevent Ad Sync from advertising the position.

```mermaid
sequenceDiagram
  participant P as Partner
  participant A as auth.seek.com
  participant G as graphql.seek.com

  G-->>P: Webhook with Job Posting event & object identifier
  P->>A: Send client credentials
  A-->>P: Issue partner token
  P->>G: Request PostedPositionProfile via GraphQL
  G-->>P: Return PostedPositionProfile
  P->>P: Correlate to job requisition
  P->>P: Display in job ad list view
```

## Posting sources

Your software must differentiate job ads that were automatically posted by Ad Sync from those that a hirer manually posted.

It is recommended to drive this differentiation off of the [`seekPostingSourceCode`] available when querying the state of a `PostedPositionProfile`, otherwise you can use a posting source value that your software stores at the time of posting.

Include an `(Ad Sync)` qualifier in the channel or source field of your software.
Your software may also support advanced sorting and filtering by this field.

<AdSyncSource />

If this is not feasible, your software may add an `(Ad Sync)` prefix to the title field of the job ad.

<AdSyncPrefix />

When querying the state of a `PostedPositionProfile`,
your software will receive different `seekCreatedBySelfIndicator` and `seekPostingSourceCode` values depending on the posting source.

```jsonc label="Ad Sync via SEEK API"
// Treat as automated posting
{
  "seekCreatedBySelfIndicator": true,
  "seekPostingSourceCode": "AdSync"
}
```

```jsonc label="Ad Sync via feeds & web scraping"
// Treat as automated posting
{
  "seekCreatedBySelfIndicator": false,
  "seekPostingSourceCode": "AdSync"
}
```

```jsonc label="Job Posting via SEEK API"
// Treat as manual posting
{
  "seekCreatedBySelfIndicator": true,
  "seekPostingSourceCode": "CurrentPartner"
}
```

```jsonc label="SEEK Employer website"
// Treat as manual posting
{
  "seekCreatedBySelfIndicator": false,
  "seekPostingSourceCode": "Seek"
}
```

## Lifecycle events

Subscribe to `PositionNotPosted`, `PositionProfilePosted`, `PositionProfileUpdated`, and `PositionProfileClosed` events to keep your software in sync.
See our [event] documentation for more context.

```mermaid
flowchart TD

Start@{ shape: circle, label: "Post" }
PositionNotPosted@{ shape: braces, label: "PositionNotPosted" }
PositionProfilePosted@{ shape: braces, label: "PositionProfilePosted" }
PositionProfileUpdated@{ shape: braces, label: "PositionProfileUpdated" }
PositionProfileUpdated2@{ shape: braces, label: "PositionProfileUpdated" }
PositionProfileClosed@{ shape: braces, label: "PositionProfileClosed" }

Start --> PostDecision{pass sync check?}

PostDecision --> |no| PositionNotPosted -.-> NotPosted[Not Posted]
PostDecision ---> |yes| PositionProfilePosted -.-> Active[Active]

NotPosted --> |update| PostDecision
NotPosted --> |expire| PositionProfileClosed

Active --> |update| PositionProfileUpdated -.-> Active
Active ---> |fail async check| PositionProfileUpdated2 -.-> Suspended[Suspended]
Active ---> |expire| PositionProfileClosed

Suspended ---> |reactivate| PositionProfileUpdated
Suspended ----> |expire| PositionProfileClosed

PositionProfileClosed -.-> Closed[Closed]
```

### PositionNotPosted

A [PositionNotPosted] event will be emitted whenever a position cannot be posted on SEEK, whether due to insufficient information in the [`syncPositions` mutation], an issue with the hirer’s SEEK account or Ad Sync configuration, an intentional exclusion via the hirer’s Ad Sync rules, or following a job ad being edited outside of Ad Sync (e.g. directly on the SEEK Employer website or through Classic or Enhanced Job Posting), which prevents future Ad Sync updates from propagating to the job ad.

Your software may surface a SEEK provided message explaining why the position wasn’t posted and guidance on how to resolve it.

<AdSyncNotPosted />

To determine why a position was not posted, use the `reasonId` included in the [PositionNotPosted] event to query [positionNotPostedReason]. This will return a detailed explanation and suggested actions for the specific posting failure.

To retrieve a list of historical reasons associated with a position, use the `seekPartnerPositionId` included in the [PositionNotPosted] event to query [positionNotPostedReasons].

To test how your software handles [PositionNotPosted] events, include a supported key in `positionTitle` when calling the [`syncPositions` mutation].

For example, setting `positionTitle` to include `EXPIRED_AD` will prevent the job ad from being posted on SEEK and emit a [PositionNotPosted] event.

```json label="Input snippet"
{
  "positionTitle": "Senior Business Analyst EXPIRED_AD"
}
```

Some example keys you can use to test different scenarios:

- `CATEGORY_INFERENCE_FAILED`
- `EXPIRED_AD`
- `LOCATION_INFERENCE_FAILED`
- `SALARY_INFERENCE_FAILED`

<Note /> These keys are for **testing purposes only**. The content and mapping
of these codes is subject to change and should not be relied upon.

### PositionProfilePosted

Once a job ad has been posted,
provide options to view the job ad on SEEK,
and to manage the job ad via classic or Enhanced Job Posting.
The latter allows hirers to further tailor the content of job ads posted via Ad Sync.

<AdSyncPosted />

### PositionProfileUpdated

Hirers can use the SEEK Employer website or a classic or Enhanced Job Posting integration to edit job ads posted via Ad Sync.
Keep the details in your software in sync with these changes.

{/* prettier-ignore */}
<Directional /> A new `PostedPositionProfile.seekStatusCode` field is available for your software to select.
This will be `Active` while the job ad is listed on our employment marketplace.

<AdSyncUpdated />

{/* prettier-ignore */}
<Directional /> In rare cases, SEEK may flag a job ad after it has been posted, move it to a `Suspended` status, and emit another `PositionProfileUpdated` event.

This may be caused by duplicate job ads being posted under the same hirer account, or issues with the job ad content.

Your software may include a call to action for hirers to reactivate suspended job ads by contacting SEEK.

{/* prettier-ignore */}
<Directional /> Ad Sync may be extended in a later release to emit a [PositionNotPosted] event when an update to a position profile has failed.

<AdSyncSuspended />

### PositionProfileClosed

Expiration is a terminal state for the job ad.
Disable the action to view it on SEEK.

{/* prettier-ignore */}
<Directional /> Provide an option to re-post the job ad via Enhanced Job Posting, once available.

<AdSyncClosed />

[`seekPostingSourceCode`]: /schema/named-type/PostedPositionProfile#/field/seekPostingSourceCode
[`syncPositions` mutation]: /schema/mutation/syncPositions
[classic]: /use-cases/job-posting/classic
[duplication]: /use-cases/job-posting/job-ad-duplication
[Enhanced Job Posting]: /use-cases/job-posting/enhanced
[event]: /events
[GraphQL endpoint]: /graphql
[identifiers & references]: /use-cases/job-posting/identifiers-and-references#reference-fields
[synced a position]: /use-cases/job-posting/ad-sync/sync-positions
[rules]: /use-cases/job-posting/ad-sync#seek-posting-configuration
[PositionNotPosted]: /schema/webhooks/PositionNotPostedEvent
[positionNotPostedReason]: /use-cases/job-posting/ad-sync/query-position-not-posted-reason#positionnotpostedreason
[positionNotPostedReasons]: /use-cases/job-posting/ad-sync/query-position-not-posted-reason#positionnotpostedreasons
